(() => {
  if (customElements.get('sp-reveal')) return;

  class SpReveal extends HTMLElement {
    connectedCallback() {
      // A previously registered element may connect before its children are parsed.
      this.observer = new MutationObserver(() => this.initialise());
      this.observer.observe(this, { childList: true, subtree: true });
      this.initialise();
    }

    initialise() {
      if (this.events || !this.isConnected) return;
      this.button = this.querySelector('.spro-button');
      if (!this.button?.querySelector('.spro-show-more') || !this.button.querySelector('.spro-show-less')) return;
      this.observer.disconnect();
      this.bodies = [...this.querySelectorAll('.spro-card__body')];
      this.events = new AbortController();
      this.animations = new Map();
      const options = { signal: this.events.signal };
      this.button.addEventListener('click', () => this.setExpanded(!this.classList.contains('spro-active')), options);
      // Custom elements also initialise when Shopify replaces a section in the editor.
      this.addEventListener('shopify:block:select', event => {
        this.setExpanded(true);
        const index = this.cards.indexOf(event.target.closest('.spro-col'));
        if (index >= 0) this.goToCard(index);
      }, options);
      this.initialiseCarousel(options);
      this.setExpanded(false, false);
      this.dataset.ready = '';
      this.button.hidden = !this.bodies.length;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.events?.abort();
      this.events = null;
      this.animations?.forEach(animation => animation.cancel());
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
    }

    initialiseCarousel(options) {
      this.grid = this.querySelector('.spro-grid');
      this.cards = [...this.grid.querySelectorAll(':scope > .spro-col')];
      this.activeCard = 0;
      this.mobile = matchMedia('(max-width: 749px)');
      this.pagination = this.querySelector('.spro-pagination') || document.createElement('div');
      this.pagination.className = 'spro-pagination';
      this.pagination.setAttribute('role', 'group');
      this.pagination.setAttribute('aria-label', 'Feature pagination');
      this.pagination.replaceChildren();
      this.dots = this.cards.map((card, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        const title = card.querySelector('.spro-card__title')?.textContent.trim();
        dot.setAttribute('aria-label', `Show feature ${index + 1} of ${this.cards.length}${title ? `: ${title}` : ''}`);
        dot.setAttribute('aria-controls', this.grid.id);
        dot.addEventListener('click', () => this.goToCard(index), options);
        this.pagination.append(dot);
        return dot;
      });
      this.grid.after(this.pagination);
      this.grid.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateActiveCard());
      }, { ...options, passive: true });
      this.grid.addEventListener('keydown', event => {
        if (!this.mobile.matches || event.target !== this.grid) return;
        const direction = getComputedStyle(this.grid).direction === 'rtl' ? -1 : 1;
        const targets = { ArrowRight: this.activeCard + direction, ArrowLeft: this.activeCard - direction, Home: 0, End: this.cards.length - 1 };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.goToCard(targets[event.key]);
      }, options);
      const updateMode = () => {
        const enabled = this.mobile.matches && this.cards.length > 1;
        this.pagination.hidden = !enabled;
        if (enabled) {
          this.grid.tabIndex = 0;
          this.grid.setAttribute('role', 'region');
          this.grid.setAttribute('aria-roledescription', 'carousel');
          this.grid.setAttribute('aria-label', this.querySelector('.reveal-header-text h2')?.textContent.trim() || 'Feature cards');
          this.goToCard(this.activeCard, false);
        } else {
          ['tabindex', 'role', 'aria-roledescription', 'aria-label'].forEach(name => this.grid.removeAttribute(name));
          this.grid.scrollTo({ left: 0, behavior: 'instant' });
        }
        this.updateActiveCard();
      };
      this.mobile.addEventListener('change', updateMode, options);
      let width;
      this.resizeObserver = new ResizeObserver(entries => {
        const nextWidth = entries[0].contentRect.width;
        if (width === nextWidth) return;
        width = nextWidth;
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.goToCard(this.activeCard, false));
      });
      this.resizeObserver.observe(this.grid);
      updateMode();
    }

    cardScrollPosition(index) {
      const grid = this.grid.getBoundingClientRect();
      const card = this.cards[index].getBoundingClientRect();
      const style = getComputedStyle(this.grid);
      const rtl = style.direction === 'rtl';
      const inset = parseFloat(style.scrollPaddingInlineStart) || 0;
      const position = this.grid.scrollLeft + (rtl ? card.right - grid.right + inset : card.left - grid.left - inset);
      const maximum = this.grid.scrollWidth - this.grid.clientWidth;
      return rtl ? Math.max(-maximum, Math.min(0, position)) : Math.max(0, Math.min(maximum, position));
    }

    goToCard(index, smooth = true) {
      if (!this.mobile.matches || !this.cards.length) return;
      index = Math.max(0, Math.min(this.cards.length - 1, index));
      this.grid.scrollTo({
        left: this.cardScrollPosition(index),
        behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'
      });
    }

    updateActiveCard() {
      if (!this.mobile.matches || !this.cards.length) return;
      this.activeCard = this.cards.reduce((closest, card, index) =>
        Math.abs(this.cardScrollPosition(index) - this.grid.scrollLeft) < Math.abs(this.cardScrollPosition(closest) - this.grid.scrollLeft) ? index : closest, 0);
      this.dots.forEach((dot, index) => {
        if (index === this.activeCard) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    setExpanded(expanded, animate = true) {
      const motion = animate && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      const heights = this.bodies.map(body => body.getBoundingClientRect().height);
      this.animations.forEach(animation => animation.cancel());
      this.animations.clear();
      this.classList.toggle('spro-active', expanded);
      this.button.setAttribute('aria-expanded', String(expanded));
      this.button.querySelector('.spro-show-more').hidden = expanded;
      this.button.querySelector('.spro-show-less').hidden = !expanded;

      this.bodies.forEach((body, index) => {
        const content = body.querySelector('.spro-card__content');
        // Never leave keyboard focus inside content that is about to be hidden.
        if (!expanded && content.contains(document.activeElement)) this.button.focus();
        body.querySelector('.spro-card__preview').hidden = expanded;
        content.hidden = !expanded;
        const height = body.getBoundingClientRect().height;
        if (!motion || heights[index] === height) return;
        const animation = body.animate(
          [{ height: `${heights[index]}px` }, { height: `${height}px` }],
          { duration: 260, easing: 'ease-out' }
        );
        this.animations.set(body, animation);
        animation.onfinish = () => this.animations.delete(body);
      });

      if (animate && !expanded) {
        // Collapsing long cards can move the control above the viewport.
        Promise.allSettled([...this.animations.values()].map(animation => animation.finished)).then(() => {
          if (!this.isConnected || this.classList.contains('spro-active')) return;
          if (this.button.getBoundingClientRect().top < 0) {
            this.button.scrollIntoView({ behavior: motion ? 'smooth' : 'instant', block: 'center' });
          }
        });
      }
    }
  }

  customElements.define('sp-reveal', SpReveal);
})();
